import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AudioCaptureService {
  private audioContext!: AudioContext;
  private scriptProcessor!: ScriptProcessorNode;
  private stream!: MediaStream;
  private audioChunks: Float32Array[] = [];
  public onstop?: (audioBlob: Blob) => void; // Callback function when recording stops

  async startRecording() {
    try {
      this.audioContext = new AudioContext();
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const source = this.audioContext.createMediaStreamSource(this.stream);

      this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
      source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);

      this.scriptProcessor.onaudioprocess = (event) => {
        const audioData = event.inputBuffer.getChannelData(0);
        this.audioChunks.push(new Float32Array(audioData));
      };
    } catch (error) {
      console.error('Microphone access error:', error);
    }
  }

  stopRecording() {
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    const audioBlob = this.convertToWav();

    // Call the `onstop` callback if it exists
    if (this.onstop) {
      this.onstop(audioBlob);
    }
  }

  private convertToWav(): Blob {
    const sampleRate = this.audioContext.sampleRate;
    const mergedAudio = this.mergeAudioChunks();
    const wavBuffer = this.createWavFile(mergedAudio, sampleRate);
    return new Blob([wavBuffer], { type: 'audio/wav' });
  }

  private mergeAudioChunks(): Float32Array {
    let length = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
    let result = new Float32Array(length);
    let offset = 0;

    for (let chunk of this.audioChunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  private createWavFile(audioData: Float32Array, sampleRate: number): ArrayBuffer {
    const numChannels = 1;
    const bytesPerSample = 2;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);

    // WAV Header
    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, 36 + audioData.length * bytesPerSample, true);
    view.setUint32(8, 0x57415645, false); // "WAVE"
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true); // Subchunk1Size
    view.setUint16(20, 1, true); // AudioFormat (PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true); // BitsPerSample
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, audioData.length * bytesPerSample, true);

    // Convert audio data to 16-bit PCM
    const wavBuffer = new ArrayBuffer(44 + audioData.length * bytesPerSample);
    const wavView = new DataView(wavBuffer);
    new Uint8Array(wavBuffer).set(new Uint8Array(wavHeader));

    let offset = 44;
    for (let i = 0; i < audioData.length; i++, offset += 2) {
      let sample = Math.max(-1, Math.min(1, audioData[i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      wavView.setInt16(offset, sample, true);
    }

    return wavBuffer;
  }
}
